import classNames from "classnames";

type SkeletonMarker = {
  left: string;
  nameWidths: number[];
  dotCount: number;
  dateWidth?: number;
  isToday?: boolean;
};

const SKELETON_MARKERS: SkeletonMarker[] = [
  { left: "19%", nameWidths: [84], dotCount: 1, dateWidth: 32 },
  { left: "33%", nameWidths: [70], dotCount: 1, dateWidth: 36 },
  { left: "48%", nameWidths: [40], dotCount: 0, isToday: true },
  { left: "72%", nameWidths: [92, 76, 88], dotCount: 3, dateWidth: 34 },
];

const LEGEND_WIDTHS = [78, 66, 62, 48, 12];
const MONTH_COUNT = 12;
const ELAPSED_FILL_WIDTH = "48%";

const BLOCK_CLASS =
  "t-animate-skeleton-shimmer t-rounded t-bg-skeleton-shimmer t-bg-[length:200%_100%] motion-reduce:t-animate-none";

const SkeletonBlock = ({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => <span className={classNames(BLOCK_CLASS, className)} style={style} />;

export const TimelineSkeleton = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading filings timeline"
    className="t-overflow-hidden t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface"
  >
    <div className="t-flex t-flex-wrap t-items-center t-justify-between t-gap-y-2 t-px-5 t-pb-5 t-pt-[18px]">
      <SkeletonBlock className="t-h-4 t-w-[52px]" />
      <span className="t-flex t-flex-wrap t-items-center t-gap-4">
        {LEGEND_WIDTHS.map((width) => (
          <SkeletonBlock key={width} className="t-h-2.5" style={{ width }} />
        ))}
      </span>
    </div>

    <div className="hide-scrollbar t-overflow-x-auto">
      <div className="t-relative t-h-[170px] t-min-w-[560px]">
        <div className="t-absolute t-bottom-[63px] t-left-6 t-right-6 t-h-0.5 t-overflow-hidden t-rounded-sm t-bg-neutral-10">
          <span
            className="t-block t-h-full t-rounded-sm t-bg-skeleton-fill"
            style={{ width: ELAPSED_FILL_WIDTH }}
          />
        </div>

        {SKELETON_MARKERS.map((marker) => (
          <div
            key={marker.left}
            className="t-absolute t-inset-y-0 t-w-0"
            style={{ left: marker.left }}
          >
            <span className="t-absolute t-bottom-[85px] t-flex -t-translate-x-1/2 t-flex-col t-items-center t-gap-1.5">
              {marker.nameWidths.map((width) => (
                <SkeletonBlock
                  key={width}
                  className="t-h-2"
                  style={{ width }}
                />
              ))}
            </span>

            {marker.isToday ? (
              <SkeletonBlock className="t-absolute t-bottom-[68px] t-h-3 t-w-0.5 -t-translate-x-1/2" />
            ) : (
              <span className="t-absolute t-bottom-[74px] t-flex -t-translate-x-1/2 t-translate-y-1/2 t-items-center">
                {Array.from({ length: marker.dotCount }).map((_, dotIndex) => (
                  <SkeletonBlock
                    key={marker.left + dotIndex}
                    className="-t-ml-px t-h-[11px] t-w-[11px] t-rounded-full first:t-ml-0"
                  />
                ))}
              </span>
            )}

            {Boolean(marker.dateWidth) && (
              <SkeletonBlock
                className="t-absolute t-bottom-[54px] t-h-2 -t-translate-x-1/2"
                style={{ width: marker.dateWidth }}
              />
            )}
          </div>
        ))}

        <div className="t-absolute t-bottom-1 t-left-6 t-right-6 t-flex t-justify-between">
          {Array.from({ length: MONTH_COUNT }).map((_, monthIndex) => (
            <SkeletonBlock key={monthIndex} className="t-h-[7px] t-w-[21px]" />
          ))}
        </div>
      </div>
    </div>

    <div className="t-flex t-h-[76px] t-items-center t-border-0 t-border-t t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-p-5">
      <SkeletonBlock className="t-h-3 t-w-44" />
    </div>
  </div>
);
